<template>
  <div class="tutorial-features">
    <div class="space-y-6">
      <!-- Feature Showcase -->
      <div class="bg-white rounded-lg border border-gray-200 p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:star-20-solid" class="w-5 h-5 text-yellow-500" />
          Available Features
        </h4>

        <!-- Interactive Feature Cards -->
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="feature in features"
            :key="feature.id"
            @click="selectFeature(feature)"
            :class="[
              'p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-lg',
              selectedFeature?.id === feature.id
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:border-gray-300'
            ]"
          >
            <div class="text-center">
              <IconifyIcon :icon="feature.icon" :class="`w-12 h-12 mx-auto mb-3 ${feature.color}`" />
              <h5 class="font-semibold text-gray-900 mb-2">{{ feature.title }}</h5>
              <p class="text-sm text-gray-600 mb-3">{{ feature.description }}</p>
              <div class="flex items-center justify-center gap-1">
                <IconifyIcon icon="heroicons:check-20-solid" class="w-4 h-4 text-green-500" />
                <span class="text-xs text-green-600 font-medium">{{ feature.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feature Details -->
      <div v-if="selectedFeature" class="bg-white rounded-lg border border-gray-200 p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon :icon="selectedFeature.icon" :class="`w-5 h-5 ${selectedFeature.color}`" />
          {{ selectedFeature.title }} Details
        </h4>

        <div class="grid md:grid-cols-2 gap-6">
          <!-- Feature Info -->
          <div class="space-y-4">
            <div>
              <h5 class="font-medium text-gray-900 mb-2">What you can do:</h5>
              <ul class="space-y-2">
                <li
                  v-for="capability in selectedFeature.capabilities"
                  :key="capability"
                  class="flex items-start gap-2"
                >
                  <IconifyIcon icon="heroicons:check-circle-20-solid" class="w-5 h-5 text-green-500 mt-0.5" />
                  <span class="text-sm text-gray-700">{{ capability }}</span>
                </li>
              </ul>
            </div>

            <div class="bg-gray-50 rounded-lg p-4">
              <h5 class="font-medium text-gray-900 mb-2">Use Cases:</h5>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="useCase in selectedFeature.useCases"
                  :key="useCase"
                  class="px-3 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-700"
                >
                  {{ useCase }}
                </span>
              </div>
            </div>
          </div>

          <!-- Interactive Demo -->
          <div>
            <h5 class="font-medium text-gray-900 mb-2">Try it out:</h5>
            <div class="bg-gray-50 rounded-lg p-4">
              <!-- Feature-specific demo content -->
              <component
                :is="selectedFeature.demoComponent"
                v-bind="selectedFeature.demoProps"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Performance & Limits -->
      <div class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:chart-bar-20-solid" class="w-5 h-5 text-indigo-600" />
          Performance & Limits
        </h4>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div class="bg-white rounded-lg p-4 text-center">
            <IconifyIcon icon="heroicons:users-20-solid" class="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <h5 class="font-semibold text-gray-900">2-10 Users</h5>
            <p class="text-sm text-gray-600">Optimal peer count</p>
          </div>
          <div class="bg-white rounded-lg p-4 text-center">
            <IconifyIcon icon="heroicons:clock-20-solid" class="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h5 class="font-semibold text-gray-900">&lt;100ms Latency</h5>
            <p class="text-sm text-gray-600">Direct connection</p>
          </div>
          <div class="bg-white rounded-lg p-4 text-center">
            <IconifyIcon icon="heroicons:cloud-20-solid" class="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <h5 class="font-semibold text-gray-900">100MB Files</h5>
            <p class="text-sm text-gray-600">Max transfer size</p>
          </div>
          <div class="bg-white rounded-lg p-4 text-center">
            <IconifyIcon icon="heroicons:video-camera-20-solid" class="w-8 h-8 text-red-600 mx-auto mb-2" />
            <h5 class="font-semibold text-gray-900">1080p Video</h5>
            <p class="text-sm text-gray-600">HD quality streams</p>
          </div>
        </div>
      </div>

      <!-- Security Features -->
      <div class="bg-green-50 rounded-lg p-6">
        <h4 class="font-semibold text-green-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:shield-check-20-solid" class="w-5 h-5 text-green-600" />
          Security & Privacy
        </h4>

        <div class="grid md:grid-cols-2 gap-6">
          <div class="space-y-3">
            <div class="flex items-start gap-3">
              <IconifyIcon icon="heroicons:lock-closed-20-solid" class="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h5 class="font-medium text-green-900">End-to-End Encryption</h5>
                <p class="text-sm text-green-700">All data encrypted with DTLS-SRTP</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <IconifyIcon icon="heroicons:server-20-solid" class="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h5 class="font-medium text-green-900">No Server Storage</h5>
                <p class="text-sm text-green-700">Data never passes through our servers</p>
              </div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="flex items-start gap-3">
              <IconifyIcon icon="heroicons:key-20-solid" class="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h5 class="font-medium text-green-900">Perfect Forward Secrecy</h5>
                <p class="text-sm text-green-700">Unique encryption keys per session</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <IconifyIcon icon="heroicons:fingerprint-20-solid" class="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h5 class="font-medium text-green-900">Identity Verification</h5>
                <p class="text-sm text-green-700">Certificate-based authentication</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action -->
      <div class="bg-indigo-600 text-white rounded-lg p-6 text-center">
        <IconifyIcon icon="heroicons:rocket-launch-20-solid" class="w-12 h-12 mx-auto mb-4" />
        <h4 class="text-xl font-bold mb-2">Ready to Start Using WebRTC QR?</h4>
        <p class="text-indigo-100 mb-6">
          You now have everything you need to create secure, serverless peer-to-peer connections!
        </p>
        <div class="flex items-center justify-center gap-4">
          <button
            @click="startDemo('host')"
            class="px-6 py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <IconifyIcon icon="heroicons:server-20-solid" class="w-5 h-5" />
            Start as Host
          </button>
          <button
            @click="startDemo('client')"
            class="px-6 py-3 bg-indigo-700 text-white rounded-lg font-medium hover:bg-indigo-800 transition-colors flex items-center gap-2"
          >
            <IconifyIcon icon="heroicons:device-phone-mobile-20-solid" class="w-5 h-5" />
            Join as Client
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import IconifyIcon from '../IconifyIcon.vue'
import ChatDemo from './feature-demos/ChatDemo.vue'
import FileTransferDemo from './feature-demos/FileTransferDemo.vue'
import VideoDemo from './feature-demos/VideoDemo.vue'

// Props
const props = defineProps<{
  onSelectFeature?: (feature: string) => void
}>()

// Reactive state
const selectedFeature = ref<Feature | null>(null)

// Features data
type Feature = (typeof features.value)[number]

const features = ref([
  {
    id: 'chat',
    title: 'Real-time Chat',
    description: 'Instant messaging with typing indicators',
    icon: 'heroicons:chat-bubble-left-right-20-solid',
    color: 'text-blue-600',
    status: 'Available',
    capabilities: [
      'Send text messages instantly',
      'See when others are typing',
      'Share emojis and reactions',
      'Full chat history preserved'
    ],
    useCases: ['Team chat', 'Customer support', 'Live events', 'Education'],
    demoComponent: ChatDemo,
    demoProps: { interactive: true }
  },
  {
    id: 'files',
    title: 'File Transfer',
    description: 'Share files directly between devices',
    icon: 'heroicons:document-arrow-down-20-solid',
    color: 'text-green-600',
    status: 'Available',
    capabilities: [
      'Drag and drop file sharing',
      'Transfer files up to 100MB',
      'Resume interrupted transfers',
      'Multiple files simultaneously'
    ],
    useCases: ['Document sharing', 'Media files', 'Collaboration', 'Backup'],
    demoComponent: FileTransferDemo,
    demoProps: { interactive: true }
  },
  {
    id: 'video',
    title: 'Video Conference',
    description: 'HD video calls with screen sharing',
    icon: 'heroicons:video-camera-20-solid',
    color: 'text-purple-600',
    status: 'Available',
    capabilities: [
      'HD video quality (1080p)',
      'Audio with noise cancellation',
      'Screen sharing capability',
      'Multiple participants (2-10)'
    ],
    useCases: ['Video meetings', 'Remote work', 'Online classes', 'Telehealth'],
    demoComponent: VideoDemo,
    demoProps: { interactive: true }
  },
  {
    id: 'whiteboard',
    title: 'Collaborative Whiteboard',
    description: 'Draw and brainstorm together',
    icon: 'heroicons:pencil-square-20-solid',
    color: 'text-yellow-600',
    status: 'Coming Soon',
    capabilities: [
      'Real-time drawing sync',
      'Multiple drawing tools',
      'Shapes and text support',
      'Export whiteboard as image'
    ],
    useCases: ['Brainstorming', 'Education', 'Design review', 'Planning'],
    demoComponent: 'div',
    demoProps: {}
  },
  {
    id: 'games',
    title: 'Multiplayer Games',
    description: 'Play simple games together',
    icon: 'heroicons:gamepad-20-solid',
    color: 'text-red-600',
    status: 'Experimental',
    capabilities: [
      'Real-time game state sync',
      'Low-latency input handling',
      'Multiple game types',
      'Score tracking'
    ],
    useCases: ['Entertainment', 'Education', 'Team building', 'Icebreakers'],
    demoComponent: 'div',
    demoProps: {}
  },
  {
    id: 'code',
    title: 'Code Collaboration',
    description: 'Share and edit code together',
    icon: 'heroicons:code-bracket-20-solid',
    color: 'text-indigo-600',
    status: 'In Development',
    capabilities: [
      'Real-time code editing',
      'Syntax highlighting',
      'Multiple file support',
      'Version control integration'
    ],
    useCases: ['Pair programming', 'Code review', 'Education', 'Interviews'],
    demoComponent: 'div',
    demoProps: {}
  }
])

// Methods
const selectFeature = (feature: Feature) => {
  selectedFeature.value = feature
  if (props.onSelectFeature) {
    props.onSelectFeature(feature.id)
  }
}

const startDemo = (mode: 'host' | 'client') => {
  // This would emit an event to start the actual demo
  console.log(`Starting ${mode} demo...`)
  // In a real implementation, this would trigger navigation or modal
}
</script>

<style scoped>
.tutorial-features {
  /* Component-specific styles */
}
</style>